import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaBox, FaShoppingCart, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { productsApi, categoriesApi } from '../services/api';
import { useCart } from '../context/CartContext';
import './ProductManagement.css'; // Import specific CSS for this module

// Separate Modal component to isolate the modal functionality
const ProductFormModal = ({ show, onClose, formMode, currentProduct, categories, onSubmit, onInputChange, onImageUpload, isSubmitting }) => {
    console.log('ProductFormModal - Current Product:', currentProduct);
    console.log('ProductFormModal - Categories:', categories);
    console.log('ProductFormModal - Current CategoryId:', currentProduct.CategoryId);
    
    if (!show) return null;

    const handleSubmit = (e) => {
        e.preventDefault(); // Prevent default form submission
        onSubmit(e);
    };
    
    return (
        <div className="modal-overlay">
            <div className="modal-container">
                <div className="modal-content">
                    <h2>{formMode === 'add' ? 'Add New Product' : 'Edit Product'}</h2>
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="form-group">
                            <label>Name:</label>
                            <input
                                type="text"
                                name="name"
                                value={currentProduct.name}
                                onChange={onInputChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Category:</label>
                            <select
                                name="CategoryId"
                                value={currentProduct.CategoryId}
                                onChange={onInputChange}
                                required
                            >
                                <option value="">Select a category</option>
                                {Array.isArray(categories) && categories.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>MRP (₹):</label>
                            <input
                                type="number"
                                name="price"
                                value={currentProduct.price}
                                onChange={onInputChange}
                                required
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div className="form-group">
                            <label>Stock:</label>
                            <input
                                type="number"
                                name="stock"
                                value={currentProduct.stock}
                                onChange={onInputChange}
                                required
                                min="0"
                                step="1"
                            />
                        </div>
                        <div className="form-group">
                            <label>Description:</label>
                            <textarea
                                name="description"
                                value={currentProduct.description}
                                onChange={onInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>Image:</label>
                            <div className="image-preview-container">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={onImageUpload}
                                />
                                {currentProduct.imagePreview ? (
                                    <img
                                        src={currentProduct.imagePreview}
                                        alt="Product preview"
                                        className="image-preview"
                                    />
                                ) : currentProduct.imageUrl ? (
                                    <img
                                        src={currentProduct.imageUrl}
                                        alt="Current product"
                                        className="image-preview"
                                    />
                                ) : (
                                    <div className="image-preview-placeholder">
                                        No image selected
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="form-actions">
                            <button 
                                type="submit" 
                                className="btn-save" 
                                disabled={isSubmitting}
                            >
                                {isSubmitting 
                                    ? (formMode === 'add' ? 'Adding Product...' : 'Updating Product...') 
                                    : (formMode === 'add' ? 'Add Product' : 'Update Product')
                                }
                            </button>
                            <button 
                                type="button" 
                                className="btn-cancel" 
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Add Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    // Generate page numbers array
    const getPageNumbers = () => {
        const delta = 1; // Number of pages to show before and after current page
        const range = [];
        const rangeWithDots = [];
        let l;

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i);
            }
        }

        for (let i of range) {
            if (l) {
                if (i - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (i - l !== 1) {
                    rangeWithDots.push('...');
                }
            }
            rangeWithDots.push(i);
            l = i;
        }

        return rangeWithDots;
    };

    return (
        <div className="pagination">
            <div className="pagination-container">
                <button 
                    className="pagination-btn"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                >
                    <FaChevronLeft />
                </button>

                {/* Desktop view - show page numbers */}
                <div className="pagination-numbers desktop-only">
                    {getPageNumbers().map((pageNum, index) => (
                        <button
                            key={index}
                            className={`pagination-number ${pageNum === currentPage ? 'active' : ''} ${pageNum === '...' ? 'dots' : ''}`}
                            onClick={() => pageNum !== '...' && onPageChange(pageNum)}
                            disabled={pageNum === '...'}
                        >
                            {pageNum}
                        </button>
                    ))}
                </div>

                {/* Mobile view - show current/total */}
                <div className="pagination-info mobile-only">
                    <span>Page {currentPage} of {totalPages}</span>
                </div>

                <button 
                    className="pagination-btn"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                >
                    <FaChevronRight />
                </button>
            </div>
        </div>
    );
};

const ProductManagement = () => {
    // State for products list and form
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // State for new/edit product form
    const [formMode, setFormMode] = useState('add');
    const [currentProduct, setCurrentProduct] = useState({
        id: null,
        name: '',
        CategoryId: '',
        price: '',
        stock: '',
        description: '',
        imageFile: null,
        imagePreview: null
    });
    
    // State for search and filter
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    
    // State for showing form modal
    const [showForm, setShowForm] = useState(false);

    const { addToCart } = useCart();

    const [currentPage, setCurrentPage] = useState(() => {
        const savedPage = localStorage.getItem('productManagement_page');
        return savedPage ? parseInt(savedPage, 10) : 1;
    });
    const [itemsPerPage] = useState(10);

    // Filter products based on search term and category
    const filteredProducts = Array.isArray(products) ? products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (product.description || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === '' || product.Category?.name === filterCategory;
        return matchesSearch && matchesCategory;
    }) : [];
    
    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    // Reset to first page when search or filter changes
    useEffect(() => {
        if (searchTerm || filterCategory) {
            setCurrentPage(1);
        }
    }, [searchTerm, filterCategory]);

    // Save scroll position and page when leaving the page
    useEffect(() => {
        const handleBeforeUnload = () => {
            const currentPath = window.location.pathname;
            // Only clear storage if we're navigating away from product management
            if (!currentPath.includes('/products')) {
                localStorage.removeItem('productManagement_page');
            }
        };

        const handlePopState = () => {
            const currentPath = window.location.pathname;
            if (!currentPath.includes('/products')) {
                localStorage.removeItem('productManagement_page');
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
            handleBeforeUnload();
        };
    }, []);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        localStorage.setItem('productManagement_page', pageNumber.toString());
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const formatImageUrl = (imageUrl) => {
        const defaultPlaceholder = 'https://placehold.co/400x400?text=No+Image';
        
        try {
            if (!imageUrl) return defaultPlaceholder;
            
            // If it's an S3 URL, return it directly without any modification
            if (imageUrl.includes('s3.amazonaws.com')) {
                return imageUrl;
            }
            
            // If it's already a full URL (non-S3), return it directly
            if (imageUrl.startsWith('http')) {
                return imageUrl;
            }

            // For backward compatibility with old local storage URLs
            const cleanUrl = imageUrl
                .replace(/['"]/g, '')
                .replace(/;/g, '')
                .replace(/^\/+/, '')
                .replace(/\/+/g, '/')
                .trim();

            let filename = cleanUrl;
            
            const patterns = [
                /^uploads\/products\/(.+)$/,
                /^products\/(.+)$/,
                /^app\/uploads\/products\/(.+)$/,
                /^(.+)$/
            ];

            for (const pattern of patterns) {
                const match = cleanUrl.match(pattern);
                if (match && match[1]) {
                    filename = match[1];
                    break;
                }
            }

            filename = filename
                .replace(/[^\w\-.]/g, '')
                .replace(/\.+/g, '.')
                .toLowerCase();

            if (!filename || filename.length < 5) return defaultPlaceholder;

            // For old local storage URLs, use the API base URL
            const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://igqds3armu.us-east-1.awsapprunner.com';
            const finalUrl = `${apiBaseUrl}/uploads/products/${filename}`;

            try {
                new URL(finalUrl);
                return finalUrl;
            } catch {
                return defaultPlaceholder;
            }
        } catch (error) {
            console.error('[ProductManagement] Error formatting URL:', error);
            return defaultPlaceholder;
        }
    };

    const handleImageError = (event) => {
        const img = event.currentTarget;
        const defaultPlaceholder = 'https://placehold.co/400x400?text=No+Image';
        if (!img.src.includes('placehold.co')) {
            img.src = defaultPlaceholder;
        }
    };

    // Use this component for product images
    const ProductImage = ({ src, alt, className }) => {
        const imageUrl = src && src.includes('s3.amazonaws.com') ? src : formatImageUrl(src);
        const isBackendUrl = imageUrl.includes(process.env.REACT_APP_API_URL) || imageUrl.includes('igqds3armu.us-east-1.awsapprunner.com');
        
        return (
            <img
                src={imageUrl}
                alt={alt || 'Product'}
                className={`${className || ''} ${!src ? 'image-load-failed' : ''}`}
                onError={handleImageError}
                loading="lazy"
                crossOrigin={isBackendUrl ? "use-credentials" : "anonymous"}
                referrerPolicy="no-referrer"
                style={{ imageRendering: 'auto' }}
            />
        );
    };

    // Fetch products and categories when component mounts
    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);
    
    // Monitor showForm state changes
    useEffect(() => {
        console.log('showForm state changed to:', showForm);
    }, [showForm]);

    // Fetch products from API
    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await productsApi.getAll();
            console.log('[ProductManagement] Raw API response:', response);
            
            const formattedProducts = Array.isArray(response.data.data.products) 
                ? response.data.data.products.map(product => ({
                    ...product,
                    price: parseFloat(product.price || 0),
                    stock: parseInt(product.stock || 0, 10)
                }))
                : [];
            
            console.log('[ProductManagement] Formatted products:', formattedProducts);
            setProducts(formattedProducts);
            setError(null);
        } catch (err) {
            console.error('[ProductManagement] Error fetching products:', err);
            setError('Failed to fetch products. Please try again later.');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch categories from API
    const fetchCategories = async () => {
        try {
            const response = await categoriesApi.getAll();
            console.log('Categories response:', response); // Debug log
            // Update to handle the correct response structure
            setCategories(Array.isArray(response.data.data.categories) ? response.data.data.categories : []);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setCategories([]); // Set empty array on error
        }
    };

    // Reset form to default state
    const resetForm = () => {
        setCurrentProduct({
            id: null,
            name: '',
            CategoryId: '',
            price: '',
            stock: '',
            description: '',
            imageFile: null,
            imagePreview: null
        });
        setFormMode('add');
    };

    // Open form for adding a new product
    const openAddForm = (e) => {
        if (e) {
            e.preventDefault();
        }
        console.log('Opening add form...');
        resetForm();
        setShowForm(true);
        console.log('showForm state set to:', true);
    };

    // Open form for editing an existing product
    const openEditForm = (product) => {
        console.log('Opening edit form with product:', product);
        // Try to get CategoryId from different possible sources
        const CategoryId = Number(product.CategoryId || product.Category?.id);
        console.log('Setting CategoryId:', CategoryId);
        
        setCurrentProduct({
            ...product,
            price: Number(product.price || 0),
            stock: Number(product.stock || 0),
            CategoryId: CategoryId || ''
        });
        setFormMode('edit');
        setShowForm(true);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        console.log(`Input change - name: ${name}, value: ${value}`);
        
        let processedValue = value;
        
        // Handle different input types
        switch (name) {
            case 'CategoryId':
                // Ensure empty string or valid number
                processedValue = value === '' ? '' : Number(value);
                console.log('Processing CategoryId:', value, 'to:', processedValue);
                break;
            case 'price':
                processedValue = value === '' ? '' : Number(value);
                break;
            case 'stock':
                processedValue = value === '' ? '' : Math.max(0, Math.floor(Number(value)));
                break;
            default:
                processedValue = value;
        }

        setCurrentProduct(prev => {
            const updated = {
                ...prev,
                [name]: processedValue
            };
            console.log('Updated current product:', updated);
            return updated;
        });
    };

    // Handle image upload
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Use FileReader to convert to data URL instead of blob URL
            const reader = new FileReader();
            reader.onloadend = () => {
                setCurrentProduct(prev => ({
                    ...prev,
                    imageFile: file,
                    imagePreview: reader.result // This will be a data URL
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Prevent double submission
        if (isSubmitting) {
            console.log('Form is already being submitted, ignoring duplicate submission');
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            console.log('Form submission - Current product state:', currentProduct);
            
            // Convert CategoryId to number and ensure it's valid
            const categoryId = Number(currentProduct.CategoryId);
            console.log('CategoryId (raw):', currentProduct.CategoryId);
            console.log('CategoryId (converted):', categoryId);
            
            if (isNaN(categoryId) || categoryId <= 0) {
                setError('Please select a valid category');
                return;
            }

            // Create FormData object
            const formData = new FormData();

            // Append all fields to FormData
            formData.append('name', currentProduct.name);
            formData.append('CategoryId', categoryId.toString());
            formData.append('price', currentProduct.price.toString());
            formData.append('stock', currentProduct.stock.toString());
            formData.append('description', currentProduct.description || '');
            formData.append('isActive', currentProduct.isActive ? 'true' : 'false');

            // Handle image upload
            if (currentProduct.imageFile) {
                formData.append('image', currentProduct.imageFile);
                console.log('Appending image file:', currentProduct.imageFile.name);
            }

            // Log FormData entries
            for (let pair of formData.entries()) {
                console.log('FormData entry:', pair[0], ':', pair[1]);
            }

            let response;
            if (formMode === 'add') {
                response = await productsApi.create(formData);
            } else {
                response = await productsApi.update(currentProduct.id, formData);
            }

            console.log('API response:', response);
            
            // Reset form and refresh products list
            setCurrentProduct({
                id: null,
                name: '',
                description: '',
                CategoryId: '',
                price: '',
                stock: '',
                maxStock: '',
                imageUrl: '',
                imagePreview: '',
                imageFile: null,
                isActive: true
            });
            
            setShowForm(false);
            await fetchProducts();
            setError(null);
        } catch (err) {
            console.error('Form submission error:', err);
            setError(err.response?.data?.message || 'Failed to save product');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Delete a product
    const deleteProduct = async (id) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                await productsApi.delete(id);
                await fetchProducts();
                setError(null);
            } catch (err) {
                setError('Failed to delete product. Please try again.');
                console.error('Error deleting product:', err);
            }
        }
    };

    const handleAddToCart = (product) => {
        addToCart({
            id: product.id,
            name: product.name,
            price: product.price,
            imageUrl: formatImageUrl(product.imageUrl)
        });
    };

    if (loading) {
        return <div className="loading">Loading products...</div>;
    }

    return (
        <div className="product-management-container">
            <h1><FaBox className="icon-space" /> Product Management</h1>
            
            {error && <div className="error-message">{error}</div>}
            
            {/* Search and Filter Bar */}
            <div className="search-filter-bar">
                <div className="search-box">
                    <FaSearch className="search-icon" />
                    <input 
                        type="text" 
                        placeholder="Search products..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                <div className="filter-box">
                    <select 
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                    >
                        <option value="">All Categories</option>
                        {Array.isArray(categories) && categories.map((category) => (
                            <option key={category.id} value={category.name}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                </div>
                
                <button className="btn-add" onClick={openAddForm}>
                    <FaPlus /> Add Product
                </button>
            </div>
            
            {/* Products Table */}
            <div className="products-table-container">
                {Array.isArray(filteredProducts) && filteredProducts.length > 0 ? (
                    <>
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Name</th>
                                    <th>Category</th>
                                    <th>MRP</th>
                                    <th>Stock</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((product) => (
                                    <tr key={product.id}>
                                        <td className="product-image-cell">
                                            <ProductImage 
                                                src={product.imageUrl} 
                                                alt={product.name} 
                                                className="product-thumbnail" 
                                            />
                                        </td>
                                        <td>{product.name}</td>
                                        <td>
                                            {(() => {
                                                if (product.Category?.name) {
                                                    return product.Category.name;
                                                }
                                                const categoryId = Number(product.categoryId);
                                                if (!isNaN(categoryId) && categoryId > 0) {
                                                    const category = categories.find(c => c.id === categoryId);
                                                    if (category) {
                                                        return category.name;
                                                    }
                                                    return `Category ID: ${categoryId}`;
                                                }
                                                return '-';
                                            })()}
                                        </td>
                                        <td>₹{parseFloat(product.price || 0).toFixed(2)}</td>
                                        <td>{parseInt(product.stock || 0, 10)}</td>
                                        <td className="actions-cell">
                                            <button className="btn-edit" onClick={() => openEditForm(product)}>
                                                <FaEdit />
                                            </button>
                                            <button className="btn-delete" onClick={() => deleteProduct(product.id)}>
                                                <FaTrash />
                                            </button>
                                            <button
                                                className="btn-cart"
                                                onClick={() => handleAddToCart(product)}
                                                title="Add to cart"
                                            >
                                                <FaShoppingCart />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {totalPages > 1 && (
                            <Pagination 
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </>
                ) : (
                    <div className="no-products">
                        {searchTerm || filterCategory ? 'No products match your search.' : 'No products found.'}
                    </div>
                )}
            </div>

            {/* Product Form Modal */}
            <ProductFormModal
                show={showForm}
                onClose={() => setShowForm(false)}
                formMode={formMode}
                currentProduct={currentProduct}
                categories={categories}
                onSubmit={handleSubmit}
                onInputChange={handleInputChange}
                onImageUpload={handleImageUpload}
                isSubmitting={isSubmitting}
            />
        </div>
    );
};

export default ProductManagement; 